import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { addWalkInStudent } from '../../api/sessionParticipants';
import api from '../../api';
import { TextInput, TextareaInput } from '../common/form';
import { apiErrorMessage } from '../../utils/apiError';

const resolveStudentId = (student) =>
    Number(student?.id ?? student?.userId ?? student?.studentId ?? 0) || null;

const AddWalkInStudent = ({ role, sessionId, batchId, excludedStudentIds = [], onSuccess, onCancel }) => {
    const { t } = useTranslation();
    const [students, setStudents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [checkinTime, setCheckinTime] = useState('');
    const [checkoutTime, setCheckoutTime] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [studentsLoadError, setStudentsLoadError] = useState(false);

    const excludedSet = useMemo(
        () => new Set((excludedStudentIds || []).map(Number).filter(Boolean)),
        [excludedStudentIds]
    );

    useEffect(() => {
        const loadStudents = async () => {
            setStudentsLoadError(false);
            try {
                const normalizedRole = (role || 'admin').toLowerCase();
                let endpoint;
                if (normalizedRole === 'coach') {
                    endpoint = `/coach/students`;
                } else {
                    endpoint = `/${normalizedRole}/users?role=STUDENT`;
                }
                const { data } = await api.get(endpoint);
                setStudents(data || []);
            } catch (err) {
                console.error('Failed to load students:', err);
                setStudents([]);
                setStudentsLoadError(true);
                setError(t('attendance.addWalkInStudent.loadFailed'));
            }
        };
        loadStudents();
    }, [role, t]);

    const matchesSearch = (student) => {
        const fullName = `${student.firstName} ${student.lastName}`.toLowerCase();
        const email = (student.email || '').toLowerCase();
        const search = searchTerm.toLowerCase();
        return fullName.includes(search) || email.includes(search);
    };

    const eligibleStudents = useMemo(
        () => students.filter((s) => {
            const sid = resolveStudentId(s);
            return sid && !excludedSet.has(sid);
        }),
        [students, excludedSet]
    );

    const filteredStudents = useMemo(
        () => eligibleStudents.filter(matchesSearch),
        [eligibleStudents, searchTerm]
    );

    const searchMatchesExcludedOnly = useMemo(() => {
        if (!searchTerm.trim()) return false;
        const matching = students.filter(matchesSearch);
        return matching.length > 0 && matching.every((s) => excludedSet.has(resolveStudentId(s)));
    }, [students, searchTerm, excludedSet]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedStudent) {
            setError(t('attendance.addWalkInStudent.selectRequired'));
            return;
        }

        const studentId = resolveStudentId(selectedStudent);
        if (!studentId || excludedSet.has(studentId)) {
            setError(t('attendance.addWalkInStudent.alreadyOnRoster'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            const payload = {
                sessionId,
                studentId,
                checkinTime: checkinTime || null,
                checkoutTime: checkoutTime || null,
                notes: notes || null,
            };

            await addWalkInStudent(role, payload);

            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            const errorMsg = apiErrorMessage(err, t('attendance.addWalkInStudent.addFailed'));
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">{t('attendance.addWalkInStudent.title')}</h2>
                <button
                    onClick={onCancel}
                    className="text-gray-500 hover:text-gray-700"
                    type="button"
                >
                    ✕
                </button>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    {!selectedStudent ? (
                        <>
                            <TextInput
                                label={t('attendance.addWalkInStudent.selectStudent')}
                                type="text"
                                placeholder={t('attendance.addWalkInStudent.searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />

                            <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                                {studentsLoadError ? null : filteredStudents.length === 0 ? (
                                    <div className="p-4 text-gray-500 text-center">
                                        {searchMatchesExcludedOnly
                                            ? t('attendance.addWalkInStudent.alreadyOnSession')
                                            : searchTerm.trim()
                                                ? t('attendance.addWalkInStudent.noMatch')
                                                : eligibleStudents.length === 0
                                                    ? t('attendance.addWalkInStudent.allOnSession')
                                                    : t('attendance.addWalkInStudent.noStudents')}
                                    </div>
                                ) : (
                                    filteredStudents.map((student) => (
                                        <div
                                            key={resolveStudentId(student)}
                                            onClick={() => setSelectedStudent(student)}
                                            className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                                        >
                                            <div className="font-medium text-gray-900">
                                                {student.firstName} {student.lastName}
                                            </div>
                                            <div className="text-sm text-gray-600">{student.email}</div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div>
                                <div className="font-medium text-gray-900">
                                    {selectedStudent.firstName} {selectedStudent.lastName}
                                </div>
                                <div className="text-sm text-gray-600">{selectedStudent.email}</div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSelectedStudent(null)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                                {t('attendance.addWalkInStudent.change')}
                            </button>
                        </div>
                    )}
                </div>

                <TextInput
                    label={t('attendance.addWalkInStudent.checkIn')}
                    type="time"
                    value={checkinTime}
                    onChange={(e) => setCheckinTime(e.target.value)}
                    note={t('attendance.addWalkInStudent.checkInHint')}
                />

                <TextInput
                    label={t('attendance.addWalkInStudent.checkOut')}
                    type="time"
                    value={checkoutTime}
                    onChange={(e) => setCheckoutTime(e.target.value)}
                    note={t('attendance.addWalkInStudent.checkOutHint')}
                />

                <TextareaInput
                    label={t('attendance.addWalkInStudent.notes')}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder={t('attendance.addWalkInStudent.notesPlaceholder')}
                />

                <div className="flex gap-3 pt-4">
                    <button
                        type="submit"
                        disabled={loading || !selectedStudent}
                        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                        {loading ? t('attendance.addWalkInStudent.adding') : t('attendance.addWalkInStudent.add')}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-6 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        {t('common.cancel')}
                    </button>
                </div>
            </form>

            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                    <strong>{t('attendance.addWalkInStudent.noteTitle')}</strong> {t('attendance.addWalkInStudent.noteBody')}
                </p>
            </div>
        </div>
    );
};

export default AddWalkInStudent;
